/** Shared types for AI email personalization (mirrored in edge `_shared`). */

export type ProfileScoreTier = 'none' | 'minimal' | 'basic' | 'rich';

export type PersonalizationConfig = {
  enabled: boolean;
  min_score: number;
};

export type PersonalizationStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'skipped'
  | 'failed';

export type PersonalizeChangeNote = {
  area: string;
  rationale: string;
};

export type ScheduledEmailPersonalization = {
  enabled: boolean;
  min_score: number;
  status: PersonalizationStatus;
  base: {
    subject: string | null;
    body_html: string | null;
  };
  result?: {
    subject: string;
    body_html: string;
    personalized_at: string;
  };
  score_at_run?: number;
  skip_reason?: string;
  error?: string;
  context_snapshot?: Record<string, unknown>;
  context_payload?: Record<string, unknown>;
  changes?: PersonalizeChangeNote[];
};

export const DEFAULT_PERSONALIZATION_CONFIG: PersonalizationConfig = {
  enabled: false,
  min_score: 50,
};

export function parsePersonalizationConfig(raw: unknown): PersonalizationConfig {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PERSONALIZATION_CONFIG };
  const o = raw as Record<string, unknown>;
  return {
    enabled: o.enabled === true,
    min_score:
      typeof o.min_score === 'number' && Number.isFinite(o.min_score)
        ? Math.max(0, Math.min(100, Math.round(o.min_score)))
        : DEFAULT_PERSONALIZATION_CONFIG.min_score,
  };
}

export function parseScheduledEmailPersonalization(
  raw: unknown,
): ScheduledEmailPersonalization | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.enabled !== true) return null;
  const base = o.base as Record<string, unknown> | undefined;
  const status = o.status as PersonalizationStatus;
  if (
    status !== 'pending' &&
    status !== 'processing' &&
    status !== 'completed' &&
    status !== 'skipped' &&
    status !== 'failed'
  ) {
    return null;
  }
  return {
    enabled: true,
    min_score:
      typeof o.min_score === 'number'
        ? Math.max(0, Math.min(100, Math.round(o.min_score)))
        : 50,
    status,
    base: {
      subject: typeof base?.subject === 'string' ? base.subject : null,
      body_html: typeof base?.body_html === 'string' ? base.body_html : null,
    },
    result:
      o.result && typeof o.result === 'object'
        ? (o.result as ScheduledEmailPersonalization['result'])
        : undefined,
    score_at_run: typeof o.score_at_run === 'number' ? o.score_at_run : undefined,
    skip_reason: typeof o.skip_reason === 'string' ? o.skip_reason : undefined,
    error: typeof o.error === 'string' ? o.error : undefined,
    context_snapshot:
      o.context_snapshot && typeof o.context_snapshot === 'object'
        ? (o.context_snapshot as Record<string, unknown>)
        : undefined,
    context_payload:
      o.context_payload && typeof o.context_payload === 'object'
        ? (o.context_payload as Record<string, unknown>)
        : undefined,
    changes: Array.isArray(o.changes)
      ? (o.changes as PersonalizeChangeNote[]).filter(
          (c) =>
            c &&
            typeof c === 'object' &&
            typeof c.area === 'string' &&
            typeof c.rationale === 'string',
        )
      : undefined,
  };
}

export function personalizationStatusLabel(
  status: PersonalizationStatus | null | undefined,
): string {
  switch (status) {
    case 'pending':
      return 'AI pending';
    case 'processing':
      return 'AI processing';
    case 'completed':
      return 'AI personalized';
    case 'skipped':
      return 'AI skipped (low profile)';
    case 'failed':
      return 'AI failed (sent base)';
    default:
      return '';
  }
}

export function tierLabel(tier: ProfileScoreTier | null | undefined): string {
  switch (tier) {
    case 'none':
      return 'None';
    case 'minimal':
      return 'Minimal';
    case 'basic':
      return 'Basic';
    case 'rich':
      return 'Rich';
    default:
      return '—';
  }
}
